// src/router/ModuleRouteGuard.jsx
//
// Gates an entire module's route subtree at once, instead of every page
// reacting to a live 402/403 from its own data fetch. Checks subscription
// (WorkspaceContext.activeModules, per-account, per SEC-011 order: before
// permission) then a coarse "can you reach this module at all" permission
// floor (AuthContext.hasPermission, already scope-aware — see its own
// getScopedPermissions()) — the exact same two data sources
// navConfig.jsx's sidebar visibility already reads via ctx.hasModule()/
// ctx.hasAnyPermission()/ctx.hasPermissionAnywhere(). Keep a module's
// entry here in sync with its navConfig.jsx visible() condition; they
// must never drift, or a visible-but-blocked (or hidden-but-reachable)
// link is the result.
//
// Renders <Outlet/> for its nested child routes on success, or
// RestrictedAccess in place of all of them otherwise. Existing per-route
// ProtectedRoute checks inside a guarded module are NOT replaced by this —
// they stay for finer, per-action permission requirements (e.g. GoLive's
// golive:manage on /golive/new vs this guard's own coarser golive:view
// floor). This is an earlier, additional gate, not a replacement.
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import RestrictedAccess from "./RestrictedAccess";

function FullPageLoader() {
  return (
    <div className="flex items-center justify-center w-screen h-screen bg-white">
      <Loader2 size={32} className="animate-spin text-blue-500" />
    </div>
  );
}

export default function ModuleRouteGuard({ moduleKey, permissions, matchAnywhere = false }) {
  const { hasAnyPermission, hasPermissionAnywhere, permissionsLoading } = useAuth();
  const { activeModules, loading, permissionsSynced } = useWorkspace();

  // Same "hold off until synced" discipline as ProtectedRoute — judging
  // activeModules/scoped permissions before either has actually loaded
  // would wrongly render RestrictedAccess for one render cycle, and unlike
  // a passive re-render, a page unmount/remount here is disruptive enough
  // to be worth a loader instead.
  if (permissionsLoading || !permissionsSynced || loading) {
    return <FullPageLoader />;
  }

  if (!activeModules.has(moduleKey)) {
    return <RestrictedAccess reason="subscription" />;
  }

  const hasAccess = matchAnywhere
    ? hasPermissionAnywhere(permissions)
    : hasAnyPermission(permissions);
  if (!hasAccess) {
    return <RestrictedAccess reason="permission" />;
  }

  return <Outlet />;
}

ModuleRouteGuard.propTypes = {
  moduleKey: PropTypes.string.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  // true: hasPermissionAnywhere (any account/project the user can reach,
  // regardless of what's currently selected) -- needed for SOW, where
  // sow:analyze/sow:view are commonly granted account-scoped. false (the
  // rest): hasAnyPermission (global + the currently-selected scope only).
  matchAnywhere: PropTypes.bool,
};
